import React, { useState, useEffect, useCallback } from 'react';
import { cases, invitations, users } from '../../services/api';
import { nomUsuari } from '../../utils/usuari';

function EnrollmentManager({ caseId, isTeamBased, onClose }) {
  const [enrollments, setEnrollments] = useState([]);
  const [invitationCodes, setInvitationCodes] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('enrollments');

  // Form states
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [creatingEnrollment, setCreatingEnrollment] = useState(false);

  // Invitation form
  const [newInvitation, setNewInvitation] = useState({
    maxUses: '',
    expiresAt: ''
  });
  const [creatingInvitation, setCreatingInvitation] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [enrollmentsRes, invitationsRes] = await Promise.all([
        cases.getEnrollments(caseId),
        cases.getInvitations(caseId)
      ]);

      setEnrollments(enrollmentsRes.data);
      setInvitationCodes(invitationsRes.data);

      // Load available users/teams based on case type
      if (isTeamBased) {
        const teamsRes = await cases.getTeams(caseId);
        // Filter out already enrolled teams
        const enrolledTeamIds = enrollmentsRes.data
          .filter(e => e.team)
          .map(e => e.team.id);
        setAvailableTeams(teamsRes.data.filter(t => !enrolledTeamIds.includes(t.id)));
      } else {
        const usersRes = await users.getAll({ role: 'analyst', limit: 100 });
        // Filter out already enrolled users
        const enrolledUserIds = enrollmentsRes.data
          .filter(e => e.user)
          .map(e => e.user.id);
        setAvailableUsers(usersRes.data.data.filter(u => !enrolledUserIds.includes(u.id)));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant les dades');
    } finally {
      setLoading(false);
    }
  }, [caseId, isTeamBased]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateEnrollment = async (e) => {
    e.preventDefault();
    if (isTeamBased && !selectedTeamId) return;
    if (!isTeamBased && !selectedUserId) return;

    try {
      setCreatingEnrollment(true);
      setError('');

      await cases.createEnrollment(caseId, {
        userId: isTeamBased ? undefined : selectedUserId,
        teamId: isTeamBased ? selectedTeamId : undefined
      });

      setSelectedUserId('');
      setSelectedTeamId('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant la inscripció');
    } finally {
      setCreatingEnrollment(false);
    }
  };

  const handleRevokeEnrollment = async (enrollmentId) => {
    if (!window.confirm('Estàs segur que vols revocar aquesta inscripció?')) return;

    try {
      setError('');
      await cases.revokeEnrollment(caseId, enrollmentId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error revocant la inscripció');
    }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    if (!window.confirm('Estàs segur que vols eliminar permanentment aquesta inscripció?')) return;

    try {
      setError('');
      await cases.deleteEnrollment(caseId, enrollmentId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant la inscripció');
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();

    try {
      setCreatingInvitation(true);
      setError('');

      await invitations.create({
        caseId,
        maxUses: newInvitation.maxUses ? parseInt(newInvitation.maxUses) : null,
        expiresAt: newInvitation.expiresAt || null
      });

      setNewInvitation({ maxUses: '', expiresAt: '' });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant el codi d\'invitació');
    } finally {
      setCreatingInvitation(false);
    }
  };

  const handleDeactivateInvitation = async (invitationId) => {
    try {
      setError('');
      await invitations.deactivate(invitationId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error desactivant la invitació');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Estàs segur que vols eliminar aquest codi d\'invitació?')) return;

    try {
      setError('');
      await invitations.delete(invitationId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant la invitació');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-surface rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content">Gestionar Inscripcions</h2>
          <button onClick={onClose} className="text-content-muted hover:text-content-secondary">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('enrollments')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'enrollments'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-content-muted hover:text-content-secondary'
              }`}
            >
              Inscripcions ({enrollments.filter(e => e.status === 'active').length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'invitations'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-content-muted hover:text-content-secondary'
              }`}
            >
              Codis d'invitació ({invitationCodes.filter(i => i.isActive).length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {activeTab === 'enrollments' && (
            <div className="space-y-4">
              {/* Add enrollment form */}
              <div className="bg-surface-subtle p-4 rounded-lg">
                <h3 className="font-medium mb-3 text-content">Afegir {isTeamBased ? 'Equip' : 'Usuari'}</h3>
                <form onSubmit={handleCreateEnrollment} className="flex space-x-3">
                  {isTeamBased ? (
                    <select
                      value={selectedTeamId}
                      onChange={(e) => setSelectedTeamId(e.target.value)}
                      className="input flex-1"
                    >
                      <option value="">Selecciona un equip...</option>
                      {availableTeams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} ({team.members?.length || 0} membres)
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="input flex-1"
                    >
                      <option value="">Selecciona un usuari...</option>
                      {availableUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.email} {user.firstName && `(${user.firstName} ${user.lastName})`}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    type="submit"
                    disabled={creatingEnrollment || (isTeamBased ? !selectedTeamId : !selectedUserId)}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {creatingEnrollment ? 'Afegint...' : 'Afegir'}
                  </button>
                </form>
              </div>

              {/* Enrollments list */}
              <div className="space-y-2">
                {enrollments.length === 0 ? (
                  <p className="text-content-muted text-center py-4">Encara no hi ha inscripcions</p>
                ) : (
                  enrollments.map(enrollment => (
                    <div
                      key={enrollment.id}
                      className={`p-3 border border-border rounded-lg flex items-center justify-between ${
                        enrollment.status === 'revoked' ? 'bg-surface-muted opacity-60' : 'bg-surface'
                      }`}
                    >
                      <div>
                        {enrollment.user ? (
                          <div>
                            <span className="font-medium text-content">{nomUsuari(enrollment.user)}</span>
                            {/* El correu, si n'hi ha i no és ja el nom que es mostra.
                                Els grups d'examen no en tenen. */}
                            {enrollment.user.email && enrollment.user.displayName && (
                              <span className="text-content-muted ml-2">{enrollment.user.email}</span>
                            )}
                          </div>
                        ) : (
                          <div>
                            <span className="font-medium text-content">{enrollment.team.name}</span>
                            <span className="text-content-muted ml-2">
                              ({enrollment.team.members?.length || 0} membres)
                            </span>
                          </div>
                        )}
                        <div className="text-sm text-content-muted">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs mr-2 ${
                            enrollment.enrollmentType === 'assigned'
                              ? 'bg-accent-subtle text-accent'
                              : 'bg-success-subtle text-success-text'
                          }`}>
                            {enrollment.enrollmentType}
                          </span>
                          {enrollment.assessmentStatus && (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                              enrollment.assessmentStatus === 'in_progress'
                                ? 'bg-warning-subtle text-warning-text'
                                : enrollment.assessmentStatus === 'submitted'
                                ? 'bg-success-subtle text-success-text'
                                : 'bg-surface-muted text-content'
                            }`}>
                              {enrollment.assessmentStatus.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {enrollment.status === 'active' ? (
                          <button
                            onClick={() => handleRevokeEnrollment(enrollment.id)}
                            className="text-yellow-600 hover:text-warning-text text-sm"
                          >
                            Revocar
                          </button>
                        ) : (
                          <span className="text-error text-sm">Revocat</span>
                        )}
                        <button
                          onClick={() => handleDeleteEnrollment(enrollment.id)}
                          className="text-red-600 hover:text-error-text text-sm"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'invitations' && (
            <div className="space-y-4">
              {/* Create invitation form */}
              <div className="bg-surface-subtle p-4 rounded-lg">
                <h3 className="font-medium mb-3 text-content">Crear codi d'invitació</h3>
                <form onSubmit={handleCreateInvitation} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-content-secondary mb-1">Màxim d'usos (opcional)</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="Il·limitat"
                        value={newInvitation.maxUses}
                        onChange={(e) => setNewInvitation({ ...newInvitation, maxUses: e.target.value })}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-content-secondary mb-1">Expira (opcional)</label>
                      <input
                        type="datetime-local"
                        value={newInvitation.expiresAt}
                        onChange={(e) => setNewInvitation({ ...newInvitation, expiresAt: e.target.value })}
                        className="input w-full"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={creatingInvitation}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {creatingInvitation ? 'Creant...' : 'Crear codi'}
                  </button>
                </form>
              </div>

              {/* Invitation codes list */}
              <div className="space-y-2">
                {invitationCodes.length === 0 ? (
                  <p className="text-content-muted text-center py-4">Encara no hi ha codis d'invitació</p>
                ) : (
                  invitationCodes.map(invitation => (
                    <div
                      key={invitation.id}
                      className={`p-3 border border-border rounded-lg ${
                        !invitation.isActive ? 'bg-surface-muted opacity-60' : 'bg-surface'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <code className="bg-surface-muted text-content px-3 py-1 rounded font-mono text-lg">
                            {invitation.code}
                          </code>
                          <button
                            onClick={() => copyToClipboard(invitation.code)}
                            className="text-content-muted hover:text-content-secondary"
                            title="Copiar codi"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex space-x-2">
                          {invitation.isActive && (
                            <button
                              onClick={() => handleDeactivateInvitation(invitation.id)}
                              className="text-yellow-600 hover:text-warning-text text-sm"
                            >
                              Desactivar
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteInvitation(invitation.id)}
                            className="text-red-600 hover:text-error-text text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-content-muted flex items-center space-x-4">
                        <span>
                          Usos: {invitation.usesCount}{invitation.maxUses ? ` / ${invitation.maxUses}` : ' (il·limitat)'}
                        </span>
                        {invitation.expiresAt && (
                          <span>
                            Expira: {new Date(invitation.expiresAt).toLocaleString()}
                          </span>
                        )}
                        {!invitation.isActive && (
                          <span className="text-error">Desactivat</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Tancar
          </button>
        </div>
      </div>
    </div>
  );
}

export default EnrollmentManager;
